"use client";

import React, { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { 
  Building2, 
  Globe, 
  KanbanSquare, 
  RotateCcw, 
  ArrowLeft, 
  Sparkles,
  Check
} from "lucide-react";

interface DemoTopBarProps {
  currency?: "INR" | "USD";
  onCurrencyChange?: (c: "INR" | "USD") => void;
  onResetComplete?: () => void;
}

export default function DemoTopBar({
  currency = "INR",
  onCurrencyChange,
  onResetComplete,
}: DemoTopBarProps) {
  const pathname = usePathname();
  const isCrm = pathname.includes("/crm");
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleReset = async () => {
    try {
      setIsResetting(true);
      const res = await fetch("/api/demos/real-estate/reset", { method: "POST" });
      if (res.ok) {
        setResetSuccess(true);
        if (onResetComplete) onResetComplete();
        setTimeout(() => setResetSuccess(false), 2500);
      }
    } catch (err) {
      console.error("Failed to reset demo:", err);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="sticky top-0 z-50 w-full bg-slate-950/95 text-slate-200 border-b border-amber-500/20 backdrop-blur-xl shadow-xl transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-3">
        {/* Left: Demo Indicator */}
        <div className="flex items-center gap-2.5">
          <Link
            href="/demos"
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-amber-400 transition-colors mr-1 group"
          >
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
            <span className="hidden md:inline">All Demos</span>
          </Link>

          <div className="h-4 w-px bg-slate-800 hidden md:block" />

          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-[11px] font-semibold tracking-wider uppercase text-amber-400/90 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-amber-400" />
              Industry Showcase
            </span>
            <span className="hidden sm:inline-block text-xs font-bold text-white bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/30">
              🏢 Real Estate & High-Yield Assets
            </span>
          </div>
        </div>

        {/* Center: View Mode Switcher */}
        <div className="flex items-center bg-slate-900/90 p-1 rounded-xl border border-slate-800 shadow-inner">
          <Link
            href="/demos/real-estate"
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              !isCrm
                ? "bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 shadow-md font-bold"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            <span>Client Website</span>
          </Link>

          <Link
            href="/demos/real-estate/crm"
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              isCrm
                ? "bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 shadow-md font-bold"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <KanbanSquare className="w-3.5 h-3.5" />
            <span>Sales CRM Pipeline</span>
            <span className="text-[9px] px-1.5 py-0.2 rounded-full bg-amber-400/20 text-amber-300 font-bold">
              Live
            </span>
          </Link>
        </div>

        {/* Right: Currency & Reset Controls */}
        <div className="flex items-center gap-2.5">
          {/* Currency Pill */}
          {onCurrencyChange && (
            <div className="flex items-center bg-slate-900 border border-slate-800 rounded-lg p-0.5 text-[11px] font-bold">
              <button
                type="button"
                onClick={() => onCurrencyChange("INR")}
                className={`px-2 py-0.5 rounded transition-all ${
                  currency === "INR"
                    ? "bg-amber-500 text-slate-950 shadow-xs"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                🇮🇳 INR (₹)
              </button>
              <button
                type="button"
                onClick={() => onCurrencyChange("USD")}
                className={`px-2 py-0.5 rounded transition-all ${
                  currency === "USD"
                    ? "bg-amber-500 text-slate-950 shadow-xs"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                🇺🇸 USD ($)
              </button>
            </div>
          )}

          {/* 1-Click Reset Demo Button */}
          <button
            type="button"
            onClick={handleReset}
            disabled={isResetting}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-[11px] font-semibold text-slate-300 hover:text-amber-400 transition-colors disabled:opacity-50"
            title="Reset demo leads back to clean showcase state"
          >
            {resetSuccess ? (
              <>
                <Check className="w-3 h-3 text-emerald-400" />
                <span className="text-emerald-400 font-bold">Reset Done!</span>
              </>
            ) : (
              <>
                <RotateCcw className={`w-3 h-3 ${isResetting ? "animate-spin text-amber-400" : ""}`} />
                <span className="hidden sm:inline">Reset Demo Leads</span>
                <span className="sm:hidden">Reset</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
